import type { IApp } from '@ohpen/react-utils';
import type { AssistantStreamChunk, AssistantTransportRequest } from '@ohpen/enso-ui';

import { OPEN_LIST_ACTION, describeApplied, describeOffer, isSettledOfferRound, offerOpenListCall, readListFilterInstruction } from './listActions';
import { runListFilter } from './listRegistry';
import { OPEN_CREATE_FORM, describeDraftApplied, readCrudDraftInstruction, type CrudDraftInstruction } from './draftActions';
import { offerCreateForm, runCrudDraft } from './crudRegistry';
import { OPEN_CLONE_TABLE, describeCloneApplied, readCrudCloneInstruction, type CrudCloneInstruction } from './cloneActions';
import { offerClone, runCrudClone } from './cloneRegistry';
import { foldRetriedSteps, type StepChunk } from './stepChain';
import { buildAgentContext, replayedContent } from './pageContext';

// Transport for the Enso Assistant, wired to the PMP agent service.
//
// The agent exposes POST /chat as text/event-stream whose `data:` payloads are already
// Enso AssistantStreamChunk objects. The agent is stateless per turn, so this transport forwards
// the current user message, the bounded prior conversation as `history`, and the live page
// context, parses the SSE frames, and yields them.
//
// It reuses the logged-in user's PMP session token (no manual paste): EnsoProvider sits
// above AuthProvider in App.tsx, so the transport cannot call useAuth() at construction.
// AuthenticatedContent registers the session token getter here once the auth context is
// available; the transport reads it at call time and forwards it to the agent, which
// forwards it to the BFF for tool calls.

let sessionTokenGetter: (() => Promise<string | null>) | null = null;

export const registerAssistantAuth = (getAccessToken: () => Promise<string | null>): void => {
  sessionTokenGetter = getAccessToken;
};

// PMP's app-specific runtime config, following IApp's own guidance to extend it per-app
// rather than add ad-hoc fields to the shared type. Populated from config.json's APP
// section (see AuthenticatedContent.tsx), the same mechanism PMP already uses for
// apiBaseurl/brokerPortalUrl.
export interface PmpAssistantAppConfig extends IApp {
  agentUrl?: string;
}

// Falls back to running the agent locally until it has a deployed URL configured.
const LOCAL_DEV_AGENT_URL = 'http://localhost:8080/chat';

let agentUrl: string = LOCAL_DEV_AGENT_URL;

export const registerAssistantAgentUrl = (configuredUrl: string | undefined): void => {
  agentUrl = configuredUrl || LOCAL_DEV_AGENT_URL;
};

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

// The request carries the caller's access token, so a deployed page only ever posts it over TLS.
// Parsing rather than a prefix test, so an unresolved "< agent_url >" config placeholder is rejected too.
function isSecureRemoteUrl(candidate: string): boolean {
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' && !LOCAL_HOSTNAMES.includes(url.hostname);
  } catch {
    return false;
  }
}

// The agent bounds the history it actually replays, so sending everything before the prompt is safe.
function buildTurn(request: AssistantTransportRequest): { message: string; history: { role: string; content: string }[] } {
  const roles = request.messages.map(entry => entry.role);
  const lastUserIndex = roles.lastIndexOf('user');
  const message = lastUserIndex >= 0 ? request.messages[lastUserIndex].content ?? '' : '';
  const history = request.messages
    .slice(0, lastUserIndex < 0 ? 0 : lastUserIndex)
    .filter(entry => entry.role === 'user' || entry.role === 'assistant')
    .map(entry => ({ role: entry.role, content: replayedContent(entry) }))
    .filter(entry => entry.content.trim() !== '');
  return { message, history };
}

// A chunk that carries an instruction for this app is carried out here and then passed on saying what
// happened, so the step the panel paints is the browser's own account of it rather than the agent's
// expectation. Every other chunk is returned untouched.
//
// The agent decides the filter; the browser decides whether the list is on screen, what the grid can honour
// and whether it worked. Nothing is applied twice: the agent sends the instruction on the completed chunk
// only. Nor is the user moved off the page they are on unless the instruction says they agreed to it.
function applyChunkAction(chunk: AssistantStreamChunk): AssistantStreamChunk {
  if (chunk.type !== 'tool-call') return chunk;

  const draft = readCrudDraftInstruction(chunk.call.arguments);
  if (draft !== null) return openDraftChunk(chunk, draft);

  const clone = readCrudCloneInstruction(chunk.call.arguments);
  if (clone !== null) return openCloneChunk(chunk, clone);

  const instruction = readListFilterInstruction(chunk.call.arguments);
  if (instruction === null) return chunk;

  const call = { ...chunk.call, arguments: {} };
  const list = instruction.list ?? instruction.entity;
  try {
    const outcome = runListFilter(instruction);
    switch (outcome.status) {
      case 'applied':
        return { type: 'tool-call', call: { ...call, status: 'success', result: describeApplied(outcome.applied, instruction.list) } };
      case 'navigating':
        return { type: 'tool-call', call: { ...call, status: 'success', result: `Opening ${list}` } };
      // Handed back to the panel as a call waiting to be approved, so the step grows the confirm affordance
      // and the user agrees with a click rather than by typing to an agent that has to remember what it
      // offered. The filter travels in the call itself.
      case 'needs-confirmation':
        return {
          type: 'tool-call',
          call: {
            ...call,
            name: OPEN_LIST_ACTION,
            arguments: offerOpenListCall(instruction),
            summary: describeOffer(instruction),
            status: 'pending',
          },
        };
      case 'unknown-columns':
        return { type: 'tool-call', call: { ...call, status: 'error', result: `${list} has no ${outcome.dropped.join(', ')}, so nothing was filtered` } };
      default:
        return { type: 'tool-call', call: { ...call, status: 'error', result: `${list} is not open, so nothing was filtered` } };
    }
  } catch {
    // The grid is the browser's business and a throw there must not kill the turn the user is reading.
    return { type: 'tool-call', call: { ...call, status: 'error', result: "Couldn't filter that list" } };
  }
}

// The proposal half of the same channel: the agent proposes the values, the browser decides whether that
// page is on screen, which of the fields the form actually has, and opens it. Nothing is submitted here or
// anywhere below: what the form ends with is the user's own Send for approval.
function openDraftChunk(chunk: AssistantStreamChunk & { type: 'tool-call' }, instruction: CrudDraftInstruction): AssistantStreamChunk {
  const call = { ...chunk.call, arguments: {} };
  const form = instruction.form ?? instruction.entity;
  try {
    const outcome = runCrudDraft(instruction);
    switch (outcome.status) {
      case 'opened':
        return { type: 'tool-call', call: { ...call, status: 'success', result: describeDraftApplied(outcome.applied, instruction.form) } };
      case 'navigating':
        return { type: 'tool-call', call: { ...call, status: 'success', result: `Opening the ${form} form` } };
      case 'needs-confirmation': {
        const offer = offerCreateForm(instruction);
        return {
          type: 'tool-call',
          call: { ...call, name: OPEN_CREATE_FORM, arguments: offer.arguments, summary: offer.summary, status: 'pending' },
        };
      }
      case 'unknown-fields':
        return {
          type: 'tool-call',
          call: { ...call, status: 'error', result: `The ${form} form has no ${outcome.dropped.join(', ')}, so nothing was filled in` },
        };
      default:
        return { type: 'tool-call', call: { ...call, status: 'error', result: `The ${form} form could not be opened` } };
    }
  } catch {
    // The form is the browser's business and a throw there must not kill the turn the user is reading.
    return { type: 'tool-call', call: { ...call, status: 'error', result: "Couldn't open that form" } };
  }
}

// The clone half of the same channel: the agent proposes what changes in the copies, the browser decides
// which rows those are, whether the clone table can carry each change, and opens it. Nothing is submitted
// here or anywhere below: what the clone table ends with is the user's own Send for approval.
function openCloneChunk(chunk: AssistantStreamChunk & { type: 'tool-call' }, instruction: CrudCloneInstruction): AssistantStreamChunk {
  const call = { ...chunk.call, arguments: {} };
  const list = instruction.list ?? instruction.entity;
  try {
    const outcome = runCrudClone(instruction);
    switch (outcome.status) {
      case 'opened':
        return { type: 'tool-call', call: { ...call, status: 'success', result: describeCloneApplied(outcome.applied, instruction.list) } };
      case 'navigating':
        return { type: 'tool-call', call: { ...call, status: 'success', result: `Opening ${list}` } };
      case 'needs-confirmation': {
        const offer = offerClone(instruction);
        return {
          type: 'tool-call',
          call: { ...call, name: OPEN_CLONE_TABLE, arguments: offer.arguments, summary: offer.summary, status: 'pending' },
        };
      }
      case 'refused':
        return { type: 'tool-call', call: { ...call, status: 'error', result: outcome.reason } };
      default:
        return { type: 'tool-call', call: { ...call, status: 'error', result: `${list} is not open, so nothing was cloned` } };
    }
  } catch {
    // The grid is the browser's business and a throw there must not kill the turn the user is reading.
    return { type: 'tool-call', call: { ...call, status: 'error', result: "Couldn't open that clone" } };
  }
}

// SSE frames are separated by a blank line; each frame's `data:` line carries one chunk as JSON.
async function* readSseChunks(body: ReadableStream<Uint8Array>): AsyncIterable<AssistantStreamChunk> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const fold = foldRetriedSteps();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      const dataLine = frame.split('\n').find(line => line.startsWith('data:'));
      if (dataLine)
        yield fold(applyChunkAction(JSON.parse(dataLine.slice(5).trim()) as AssistantStreamChunk) as StepChunk) as AssistantStreamChunk;
    }
  }
}

export async function* agentTransport(request: AssistantTransportRequest): AsyncIterable<AssistantStreamChunk> {
  // Fail clearly instead of with a confusing network error when a deployed environment has no
  // agentUrl configured. Mirrors the HARNESS_MODE host guard in bootstrap.tsx.
  const onLocalHost = LOCAL_HOSTNAMES.includes(globalThis.location.hostname);
  if (!isSecureRemoteUrl(agentUrl) && !onLocalHost) {
    yield {
      type: 'error',
      error: { message: 'The PMP agent is not yet configured for this environment.', code: 'network' },
    };
    return;
  }

  // The panel comes back once the user has settled the offer, to let the model react. There is nothing to
  // react to that the step and the grid do not already show, and asking would re-answer a question that has
  // been answered, under the answer it already gave.
  if (isSettledOfferRound(request.messages, [OPEN_LIST_ACTION, OPEN_CREATE_FORM, OPEN_CLONE_TABLE])) {
    yield { type: 'done' };
    return;
  }

  const { message, history } = buildTurn(request);
  const context = buildAgentContext(request.context);

  let token: string | null = null;
  try {
    token = sessionTokenGetter ? await sessionTokenGetter() : null;
  } catch {
    // Leave the token unset; the agent surfaces an auth error from the BFF if a tool needs it.
  }

  let response: Response;
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    response = await fetch(agentUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message, history, context }),
      signal: request.signal,
    });
  } catch (error) {
    yield { type: 'error', error: { message: `cannot reach the PMP agent: ${(error as Error).message}`, code: 'network' } };
    return;
  }

  if (!response.ok || !response.body) {
    yield { type: 'error', error: { message: `PMP agent returned HTTP ${response.status}`, code: 'network' } };
    return;
  }

  yield* readSseChunks(response.body);
}
